import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE, UPLOADS_BASE } from '../apiConfig';
import { Helmet } from 'react-helmet';



function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_BASE}/blog/${slug}`)
      .then(res => setPost(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (post) {
      document.title = post.meta_title || post.title || 'Blog';
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', post.meta_description || '');
      else {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = post.meta_description || '';
        document.head.appendChild(meta);
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) metaKeywords.setAttribute('content', post.keywords || '');
      else {
        const meta = document.createElement('meta');
        meta.name = 'keywords';
        meta.content = post.keywords || '';
        document.head.appendChild(meta);
      }
    }
  }, [post]);

  // Force header to be blue when component mounts
  useEffect(() => {
    const forceHeaderBlue = () => {
      // Add CSS directly to head to override all header styles
      const styleId = 'blogpost-header-blue';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          header, .header-top, .navbar, .modern-header, .navbar-expand-lg, .navbar-collapse, .navbar-nav {
            background: rgba(30, 60, 114, 0.95) !important;
            background-color: rgba(30, 60, 114, 0.95) !important;
            background-image: none !important;
          }
          .header-top, .navbar, .modern-header {
            background: rgba(30, 60, 114, 0.95) !important;
            background-color: rgba(30, 60, 114, 0.95) !important;
            background-image: none !important;
          }
        `;
        document.head.appendChild(style);
      }
      
      // Also apply directly to DOM elements
      const headerSelectors = [
        'header',
        '.header-top',
        '.navbar',
        '.modern-header',
        '.navbar-expand-lg',
        '.navbar-collapse',
        '.navbar-nav'
      ];
      
      headerSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          element.style.setProperty('background', 'rgba(30, 60, 114, 0.95)', 'important');
          element.style.setProperty('background-color', 'rgba(30, 60, 114, 0.95)', 'important');
          element.style.setProperty('background-image', 'none', 'important');
        });
      });
    };

    // Apply immediately
    forceHeaderBlue();
    
    // Apply after a short delay to ensure DOM is ready
    const timer = setTimeout(forceHeaderBlue, 100);
    
    // Apply multiple times to ensure it sticks
    const timer2 = setTimeout(forceHeaderBlue, 500);
    const timer3 = setTimeout(forceHeaderBlue, 1000);
    
    // Cleanup function to remove blue header when component unmounts
    const cleanup = () => {
      // Remove the CSS style tag
      const styleElement = document.getElementById('blogpost-header-blue');
      if (styleElement) {
        styleElement.remove();
      }
      
      // Reset header colors to original
      const headerSelectors = [
        'header',
        '.header-top',
        '.navbar',
        '.modern-header',
        '.navbar-expand-lg',
        '.navbar-collapse',
        '.navbar-nav'
      ];
      
      headerSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          element.style.removeProperty('background');
          element.style.removeProperty('background-color');
          element.style.removeProperty('background-image');
        });
      });
    };
    
    // Cleanup
    return cleanup;
  }, []);

  if (notFound) return <div className="container py-5 text-center text-danger">❌ Blog article not found.</div>;
  if (!post) return <div className="container py-5 text-center">⏳ Loading...</div>;

  const renderKeywords = (keywords) => {
    return keywords.split(',').map((kw, idx) => (
      <span key={idx} className="badge me-2 mb-2" style={{ background: '#eaf2fe', color: '#2064f5', fontSize: '1em', fontWeight: 600, border: '1px solid #2064f5', letterSpacing: '0.5px' }}>
        #{kw.trim()}
      </span>
    ));
  };

  const fixMediaUrls = (html) => {
    return html.replace(/src="\/uploads\//g, `src="${UPLOADS_BASE}/`);
  };

  return (
    <div className="blog-post-page">
      <div className="container py-5" style={{ background: '#f6f8fb', minHeight: '100vh' }}>
      {post && (
        <Helmet>
          <title>{post.meta_title || post.title || 'Blog'}</title>
          <meta name="description" content={post.meta_description || ''} />
          <meta name="keywords" content={post.keywords || ''} />
          <meta name="author" content={post.author || 'Unknown'} />
          <meta property="og:title" content={post.title} />
          <meta property="og:description" content={post.meta_description} />
          <meta property="og:type" content="article" />
          <meta property="og:image" content={post.image ? `${UPLOADS_BASE}/${post.image}` : ''} />
          <meta property="og:url" content={`https://petrochoice.org/blog/${post.slug}`} />
        </Helmet>
      )}
      <div className="row justify-content-center">
        <div className="col-lg-10">
          {/* Floating Back Button */}

          <button
            className="btn shadow mb-4 px-4"
            style={{
              borderRadius: '50px',
              fontWeight: '600',
              position: 'sticky',
              top: 20,
              zIndex: 10,
              boxShadow: '0 2px 8px rgba(32,100,245,0.10)',
              background: '#fff',
              color: '#2064f5',
              border: '1.5px solid #2064f5',
              transition: 'background 0.2s, color 0.2s',
            }}
            onClick={() => navigate(post.post_type === 'news' ? '/news' : '/blog')}
            onMouseOver={e => { e.currentTarget.style.background = '#2064f5'; e.currentTarget.style.color = '#fff'; }}
            onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#2064f5'; }}
          >
            <i className="bi bi-arrow-left-circle me-2" style={{ fontSize: '1.3em', color: '#2064f5', verticalAlign: '-2px' }}></i>
            Back to {post.post_type === 'news' ? 'News' : 'Blog'}
          </button>

          <div className="card shadow-lg border-0 rounded-4 overflow-hidden" style={{ backgroundColor: '#fff', border: '1.5px solid #eaf2fe' }}>
            {post.image && (
              <div
                style={{
                  position: 'relative',
                  borderTopLeftRadius: '1.5rem',
                  borderTopRightRadius: '1.5rem',
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '70vh'
                }}
              >
                {/* Blurred cover background to fill sides without visible duplication */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `url(${UPLOADS_BASE}/${post.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    filter: 'blur(24px)',
                    transform: 'scale(1.08)',
                    transformOrigin: 'center',
                    opacity: 0.85,
                    borderRadius: 'inherit'
                  }}
                />
                {/* subtle overlay to reduce background prominence */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.12) 100%)',
                    borderRadius: 'inherit'
                  }}
                />
                <img
                  src={`${UPLOADS_BASE}/${post.image}`}
                  alt={post.title}
                  className="d-block"
                  style={{
                    height: '100%',
                    width: 'auto',
                    maxWidth: '95%',
                    objectFit: 'contain',
                    display: 'block',
                    margin: '0 auto',
                    position: 'relative',
                    zIndex: 1,
                    boxShadow: '0 4px 16px rgba(32,100,245,0.10)'
                  }}
                />
              </div>
            )}

            <div className="card-body px-4 py-5">
              <div className="mb-3">
                <span className={`badge ${post.post_type === 'news' ? 'bg-success' : 'bg-primary'}`} style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                  {post.post_type === 'news' ? '📰 News Article' : '📝 Blog Post'}
                </span>
              </div>
              <h1 className="fw-bold mb-3" style={{ color: '#2064f5', fontSize: '2.3rem', letterSpacing: '-1px' }}>{post.title}</h1>
              <div className="mb-3" style={{ fontSize: '1.1rem', color: '#555' }}>
                <span className="me-3"><i className="bi bi-person-circle me-1" style={{ color: '#2064f5' }}></i>{post.author || 'Unknown'}</span>
                <span className="me-3"><i className="bi bi-calendar-event me-1" style={{ color: '#2064f5' }}></i>{post.created_at ? new Date(post.created_at).toLocaleDateString() : ''}</span>
                <span className="me-3"><i className="bi bi-tag me-1" style={{ color: '#2064f5' }}></i>{renderKeywords(post.keywords || '')}</span>
              </div>
              <hr style={{ borderTop: '1.5px solid #eaf2fe' }} />
              <div
                className="blog-content"
                style={{
                  fontSize: '1.15rem',
                  lineHeight: 1.8,
                  color: '#222',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  minHeight: '200px',
                  textAlign: 'left'
                }}
                dangerouslySetInnerHTML={{ __html: fixMediaUrls(post.content) }}
              />
            </div>
                    </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BlogPost;
